import { Modal } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { currencySymbolMap } from "common/Constants";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import moment from "moment";
import React from "react";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import styled from "styled-components";
import clientImage from "../../../assets/clientBillingimg.png";
import { CALL_FEATCH_API } from "common/API";

// ── Styled Components ─────────────────────────────────────────────

const InvoiceModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-close {
    top: 16px;
    right: 16px;
  }
`;

const InvoiceWrapper = styled.div`
  color: #1a1a1a;
  background: #fff;
  padding: 36px 40px 32px;
  max-height: 85vh;
  overflow-y: auto;
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 28px;
`;

const LogoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const OrgName = styled.div`
  font-size: 22px;
  color: #041053;
`;

const OrgSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-top: 2px;
`;

const MatterName = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #041053;
  margin-top: 4px;
`;

const MetaGrid = styled.div`
  text-align: right;
  font-size: 13px;
  line-height: 1.8;
  color: #374151;
  strong { color: #111; }
`;

const SectionDivider = styled.div`
  border-top: 1px solid #e5e7eb;
  margin: 24px 0;
`;

const TwoCol = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 24px;
`;

const ClientSection = styled.div`
  flex: 1;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ClientName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const ClientAddress = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.7;
`;

const ContactPerson = styled.div`
  text-align: right;
`;

const ContactName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const CurrencyNote = styled.div`
  font-size: 13px;
  color: #374151;
  strong { color: #111; }
`;

const DescriptionBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  color: #374151;
  margin-bottom: 28px;
  min-height: 56px;
`;

const DescLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const StaffFeeGrid = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 28px;
`;

const StaffSection = styled.div`
  flex: 1;
`;

const AttorneyLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 3px;
`;

const AttorneyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
`;

const FeeTable = styled.div`
  flex: 1;
  max-width: 340px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #374151;
  padding: 5px 0;
  border-bottom: ${(p) => (p.$last ? "none" : "1px solid #f3f4f6")};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 700;
  color: #111;
  padding: 12px 0 4px;
  border-top: 2px solid #e5e7eb;
  margin-top: 4px;
  span:last-child { color: #041053; }
`;

const CertText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: 20px 0 24px;
  line-height: 1.6;
`;

const ApprovalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 28px;
`;

const ApprovalBlock = styled.div``;

const ApprovalLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const ApprovalValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
`;

const PrintBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  padding: 11px 48px;
  background: #c2fae5;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #0d3d2a;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #a8f0d0; }
  @media print { display: none; }
`;

// ── Component ─────────────────────────────────────────────────────

const BillingInvoice = React.forwardRef(function BillingInvoice(
  {
    billingInvoiceData,
    open,
    handleClose,
    engagementDataForDirectPreview,
    formValues,
    clientId,
    matterId,
    matterData,
    taxRate,
    matterClientData
  },
  ref
) {
  const userList = useSelector(getUserList);
  const [clientRes, setClientRes] = React.useState(null);

  const { key: engagementKey, url: engagementUrl } = queryKeys.getMatterById(
    billingInvoiceData?.matterid
  );
  const { data: engagementDataRes } = useFetch(engagementKey, engagementUrl, {});

  const engagementData = engagementDataRes?.response?.data
    ? engagementDataRes?.response?.data
    : engagementDataForDirectPreview;

  const clientName =
    engagementData?.client?.clientName || billingInvoiceData?.clientname;

  const matterName =
    matterData?.jobTitle ||
    engagementData?.jobTitle ||
    billingInvoiceData?.mattername ||
    null;

  React.useEffect(() => {
    if (
      !open ||
      !billingInvoiceData?.billingid ||
      !billingInvoiceData?.clientcode
    )
      return;
    CALL_FEATCH_API(
      `insyts/billing?billingid=${billingInvoiceData.billingid}&clientid=${billingInvoiceData.clientcode}`,
      "get"
    )
      .then((res) => {
        const client = res?.response?.clientRes ?? null;
        setClientRes(client);
      })
      .catch(() => setClientRes(null));
  }, [open, billingInvoiceData?.billingid, billingInvoiceData?.clientcode]);

  const addressParts = [
    clientRes?.registeredaddress || matterClientData?.registeredaddress,
    clientRes?.city || matterClientData?.city,
    clientRes?.state || clientRes?.province || matterClientData?.state || matterClientData?.province,
    clientRes?.bdo_pin || clientRes?.pin || clientRes?.zipcode || matterClientData?.bdo_pin || matterClientData?.pin || matterClientData?.zipcode,
    clientRes?.country || matterClientData?.country,
  ].filter(Boolean).join(", ");
  const contactPerson = billingInvoiceData?.contactperson
    ? billingInvoiceData?.contactperson
    : billingInvoiceData?.contactpersonid;

  const description = billingInvoiceData?.description;
  const billingdate = billingInvoiceData?.billingdate;

  const currency =
    matterData?.financial_resourcePlan?.currency ||
    engagementData?.financial_resourcePlan?.currency ||
    billingInvoiceData?.currency ||
    "USD";

  // ── Fee calculations ──
  const cleanAmount = (value) => {
    if (!value) return 0;
    return parseFloat(String(value).replace(/[$,]/g, "")) || 0;
  };

  const fees = cleanAmount(
    formValues?.amount ||
      billingInvoiceData?.billingfee ||
      billingInvoiceData?.amount
  );

  const isVat =
    (billingInvoiceData?.vat &&
      billingInvoiceData?.vat.trim().toUpperCase() === "VAT") ||
    (formValues?.vat && formValues.vat.trim().toUpperCase() === "VAT");

  const vatRate = isVat ? Number(taxRate || 0) : 0;
  const disbursement = (billingInvoiceData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
    return sum + parseFloat(cl?.amount || 0);
  }, 0);

  const vatdisbursement = (billingInvoiceData?.claims || []).reduce(
    (sum, cl) => {
      if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
      if (cl?.taxtype !== "VAT") return sum;
      const amount = parseFloat(cl?.amount || 0);
      return sum + (amount * Number(taxRate || 0)) / 100;
    },
    0
  );

  const subcontract = (billingInvoiceData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
    return sum + parseFloat(cl?.amount || 0);
  }, 0);

  const vatsubcontract = (billingInvoiceData?.claims || []).reduce(
    (sum, cl) => {
      if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
      if (cl?.taxtype !== "VAT") return sum;
      const amount = parseFloat(cl?.amount || 0);
      return sum + (amount * Number(taxRate || 0)) / 100;
    },
    0
  );

  const vatAmount = (fees * vatRate) / 100 + vatdisbursement + vatsubcontract;
  const total = fees + disbursement + subcontract + vatAmount;

  const fmt = (n) => parseFloat(n || 0).toFixed(2);

  // ── People ──
  const partnerName = userList?.find(
    (user) => user.userid === engagementData?.leadAttorney
  )?.fullname;

  const personInCharge = userList?.find(
    (user) => user.userid === engagementData?.referringAttorney
  )?.fullname;

  const billingStatus = billingInvoiceData?.statusname;
  const approvedby = billingInvoiceData?.engagementPartner;
  const approvedbyName = userList?.find(
    (user) => user?.userid === approvedby
  )?.fullname;

  const getUserName = (userId) => {
    const user = userList?.find(
      (u) => u.userid === userId || u.email === userId
    );
    return user?.name || user?.username || user?.fullname || userId || "—";
  };

  const leadAttorneyName = billingInvoiceData?.leadattorney
    ? getUserName(billingInvoiceData.leadattorney)
    : partnerName || "—";

  const referringAttorneyName = billingInvoiceData?.referringattorney
    ? getUserName(billingInvoiceData.referringattorney)
    : personInCharge || "—";

  const approvedDate = billingInvoiceData?.billingapproveddate
    ? moment(billingInvoiceData.billingapproveddate).format("DD MMM YYYY")
    : billingdate
    ? moment(billingdate).format("DD MMM YYYY")
    : "—";

  const currencyLabel =
    currency === "USD"
      ? "US Dollar"
      : currency === "KES"
      ? "Kenyan Shilling"
      : currency;

  // ── Print ref & handler ──
  const printComponentRef = React.useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
    pageStyle: `
      @page { margin: 20mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .invoice-print-wrapper {
          max-height: none !important;
          overflow: visible !important;
        }
      }
    `,
  });

  // Expose handlePrint to parent via ref
  React.useImperativeHandle(ref, () => ({
    handlePrint,
  }));

  // ── Render ──
  // The InvoiceWrapper is always rendered (hidden when modal is closed)
  // so printComponentRef always points to real DOM — enabling silent print.
  const invoiceContent = (
    <InvoiceWrapper ref={printComponentRef} className="invoice-print-wrapper">

        {/* ── Header ── */}
        <InvoiceHeader>
          <LogoBox>
            <img
              src={clientImage}
              alt="client logo"
              style={{
                width: 80,
                height: 80,
                borderRadius: 8,
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
            <div>
              {/* <OrgName>{clientName || "—"}</OrgName> */}
              {matterName && <MatterName>{matterName}</MatterName>}
              <OrgSub>
                {billingInvoiceData?.managingoffice ||
                  matterData?.managingoffice?.[0] ||
                  matterData?.location ||
                  engagementData?.managingoffice ||
                  "—"}
              </OrgSub>
            </div>
          </LogoBox>
          <MetaGrid>
            <div>
              <strong>Date:</strong>{" "}
              {billingdate
                ? moment(billingdate).format("DD MMM YYYY")
                : "—"}
            </div>
            <div>
              <strong>Matter ID:</strong>{" "}
              {engagementData?.engagementId ||
                matterData?.matterId ||
                billingInvoiceData?.matterid ||
                "—"}
            </div>
            <div>
              <strong>Billing ID:</strong>{" "}
              {billingInvoiceData?.billingid || "—"}
            </div>
          </MetaGrid>
        </InvoiceHeader>

        {/* ── Client & Contact ── */}
        <TwoCol>
          <ClientSection>
            <SectionLabel>Client Information</SectionLabel>
            <ClientName>{clientName || "—"}</ClientName>
            <ClientAddress>
            {addressParts && (
              <ClientAddress>
                {addressParts.split("\n").map((line, i) => (
                  <span key={i}>{line}<br /></span>
                ))}
              </ClientAddress>
            )}
            </ClientAddress>
          </ClientSection>
          <ContactPerson>
            <SectionLabel>Contact Person</SectionLabel>
            <ContactName>{contactPerson || "—"}</ContactName>
            <CurrencyNote>
              Currency: <strong>{currencyLabel}</strong>
            </CurrencyNote>
          </ContactPerson>
        </TwoCol>

        {/* ── Description ── */}
        <DescLabel>Description of Services Rendered</DescLabel>
        <DescriptionBox>
          {formValues?.description || description || "—"}
        </DescriptionBox>

        {/* ── Staff Assignment + Fee Breakdown ── */}
        <StaffFeeGrid>
          <StaffSection>
            <SectionLabel>Staff Assignment</SectionLabel>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <AttorneyLabel>Lead Attorney</AttorneyLabel>
                <AttorneyName>{leadAttorneyName}</AttorneyName>
              </div>
              <div className="me-5">
                <AttorneyLabel>Referring Attorney</AttorneyLabel>
                <AttorneyName>{referringAttorneyName}</AttorneyName>
              </div>
            </div>
          </StaffSection>

          <FeeTable>
            <FeeRow>
              <span>Fees</span>
              <span>{fmt(fees)}</span>
            </FeeRow>
            <FeeRow>
              <span>Disbursement (Claims)</span>
              <span>{fmt(disbursement)}</span>
            </FeeRow>
            <FeeRow>
              <span>Subcontract (Claims)</span>
              <span>{fmt(subcontract)}</span>
            </FeeRow>
            <FeeRow>
              <span>VAT - {Number(taxRate) || 0}%</span>
              <span>{fmt(vatAmount)}</span>
            </FeeRow>
            <TotalRow>
              <span>TOTAL ({currency})</span>
              <span>
                {currencySymbolMap[currency] || currency}
                {fmt(total)}
              </span>
            </TotalRow>
          </FeeTable>
        </StaffFeeGrid>

        <SectionDivider />

        {/* ── Certification ── */}
        <CertText>
          I, certify that the above amount billed is in accordance with the
          matter and/or the assignment has been completed at client satisfaction.
        </CertText>

        {/* ── Approval ── */}
        <ApprovalRow>
          <ApprovalBlock>
            <ApprovalLabel>Approved By</ApprovalLabel>
            <ApprovalValue>
              {billingStatus === "Approved"
                ? approvedbyName ||
                  getUserName(billingInvoiceData?.billingapprovedby) ||
                  "—"
                : "—"}
            </ApprovalValue>
          </ApprovalBlock>
          <ApprovalBlock style={{ textAlign: "right" }}>
            <ApprovalLabel>Signature / Date</ApprovalLabel>
            <ApprovalValue>{approvedDate}</ApprovalValue>
          </ApprovalBlock>
        </ApprovalRow>

        {/* ── Print Button (hidden during actual print via @media print) ── */}
        <PrintBtn onClick={handlePrint}>
          <PrinterOutlined style={{ fontSize: 16 }} />
          Print Invoice
        </PrintBtn>

    </InvoiceWrapper>
  );

  return (
    <>
      {/* Hidden off-screen node always in DOM for silent printing */}
      {!open && (
        <div style={{ position: "fixed", top: -9999, left: -9999, opacity: 0, pointerEvents: "none", zIndex: -1 }}>
          {invoiceContent}
        </div>
      )}

      {/* Visible modal for Preview */}
      <InvoiceModal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={760}
        centered
        destroyOnClose={false}
      >
        {open && invoiceContent}
      </InvoiceModal>
    </>
  );
});

export default BillingInvoice;